import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { ResponsiveFlex } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { useNotificationContext } from "cerulean";

import AccountContext from "../../../components/contexts/AccountContext";
import SaveViewDialog from "../../components/dialogs/SaveViewDialog/SaveViewDialog";
import PageLayout from "../../components/PageLayout";

import { ExportDialog } from "./components/ExportDialog";
import FilterBar from "./components/FilterBar/FilterBar";
import ResultSummary from "./components/ResultSummary/ResultSummary";
import { DEFAULT_PRESET, FORMAT_DATE_DISPLAY } from "./constants";
import { useBaiTransactionTypes } from "./hooks/useBaiTransactionTypes";
import useTransactionExport from "./hooks/useTransactionExport";
import { useSavedViews } from "./hooks/useSavedViews";
import { useTransactionStats } from "./hooks/useTransactionStats";
import styles from "./TransactionExportPage.module.scss";

import type Account from "byzantine/src/Account";
import type { DatePreset, ExportFormat } from "./constants";
import type { DateRangeSelection } from "./components/DateRangeFilterPopover/DateRangeFilterPopover";

const TransactionExportPage = () => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const accountCtx = useContext(AccountContext);
  const allAccounts: Account[] = useMemo(
    () => accountCtx?.internalAccounts ?? [],
    [accountCtx?.internalAccounts]
  );

  const {
    types: transactionTypes,
    isLoading: isTypesLoading,
    error: typesError,
  } = useBaiTransactionTypes();

  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [selectedPreset, setSelectedPreset] = useState<DatePreset | null>(
    DEFAULT_PRESET
  );
  const [startDate, setStartDate] = useState(DEFAULT_PRESET.minDate());
  const [endDate, setEndDate] = useState(DEFAULT_PRESET.maxDate());
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>("bai2");
  const [selectedTypeCodes, setSelectedTypeCodes] = useState<string[]>([]);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);

  // Clear any selected type codes that no longer exist in the API response
  // (e.g., institution configuration changed between sessions)
  useEffect(() => {
    if (transactionTypes.length === 0) return;
    const availableCodes = new Set(transactionTypes.map((t) => t.code));
    setSelectedTypeCodes((prev) => {
      const validCodes = prev.filter((code) => availableCodes.has(code));
      return validCodes.length === prev.length ? prev : validCodes;
    });
  }, [transactionTypes]);

  const validAccountIds = useMemo(
    () => allAccounts.map((a) => a.id),
    [allAccounts]
  );

  const savedViewsHook = useSavedViews({
    currentFilters: {
      selectedAccountIds,
      selectedPreset,
      startDate,
      endDate,
      selectedTypeCodes,
    },
    validAccountIds,
  });

  // Derived: accounts in scope for the export.
  // When no accounts are selected the list is empty (empty-state).
  const activeAccounts = useMemo(() => {
    if (selectedAccountIds.length === 0) {
      return [];
    }
    const selectedSet = new Set(selectedAccountIds);
    return allAccounts.filter((a) => selectedSet.has(a.id));
  }, [allAccounts, selectedAccountIds]);

  const hasActiveFilters = useMemo(() => {
    const hasAccounts = selectedAccountIds.length > 0;
    const hasNonDefaultDateRange =
      selectedPreset?.value !== DEFAULT_PRESET.value;
    const hasTypeCodes = selectedTypeCodes.length > 0;
    return hasAccounts || hasNonDefaultDateRange || hasTypeCodes;
  }, [selectedAccountIds, selectedPreset, selectedTypeCodes]);

  // Date range — when no dates are set, fall back to DEFAULT_PRESET
  // so downstream hooks always receive valid dates.
  const effectivePreset = selectedPreset ?? DEFAULT_PRESET;
  const minDate = startDate !== "" ? startDate : effectivePreset.minDate();
  const maxDate = endDate !== "" ? endDate : effectivePreset.maxDate();
  const dateRangeDisplay = `${FORMAT_DATE_DISPLAY(
    minDate
  )} – ${FORMAT_DATE_DISPLAY(maxDate)}`;

  const { isExporting, handleExport } = useTransactionExport({
    accounts: activeAccounts,
    selectedFormat,
    selectedTypeCodes,
    minDate,
    maxDate,
  });

  const {
    stats,
    isLoading: isStatsLoading,
    error: statsError,
  } = useTransactionStats({
    selectedAccountIds,
    minDate,
    maxDate,
    selectedTypeCodes,
  });

  useEffect(() => {
    if (statsError) {
      sendNotification({
        type: "negative",
        text: l10n.getString("transaction-export-stats-error"),
      });
    }
  }, [statsError, sendNotification, l10n]);

  // Filters are considered "applied" when at least one account is selected
  const hasFiltersApplied = selectedAccountIds.length > 0;

  const resetDateRangeToDefault = useCallback(() => {
    setSelectedPreset(DEFAULT_PRESET);
    setStartDate(DEFAULT_PRESET.minDate());
    setEndDate(DEFAULT_PRESET.maxDate());
  }, []);

  const handleClearFilters = useCallback(() => {
    setSelectedAccountIds([]);
    resetDateRangeToDefault();
    setSelectedFormat("bai2");
    setSelectedTypeCodes([]);
    savedViewsHook.clearSelection();
  }, [resetDateRangeToDefault, savedViewsHook]);

  const handleDateRangeChange = (selection: DateRangeSelection | null) => {
    if (selection) {
      setSelectedPreset(selection.preset);
      setStartDate(selection.startDate);
      setEndDate(selection.endDate);
    } else {
      resetDateRangeToDefault();
    }
  };

  const handleDialogDownload = (fileName: string) => {
    setIsExportDialogOpen(false);
    handleExport(fileName);
  };

  const handleSelectView = useCallback(
    (viewId: string) => {
      const filterState = savedViewsHook.selectView(viewId);
      setSelectedAccountIds(filterState.selectedAccountIds);
      setSelectedPreset(filterState.selectedPreset);
      setStartDate(filterState.startDate);
      setEndDate(filterState.endDate);
      setSelectedTypeCodes(filterState.selectedTypeCodes);
    },
    [savedViewsHook]
  );

  const handleOpenSaveDialog = useCallback(() => {
    setIsSaveDialogOpen(true);
  }, []);

  const handleCloseSaveDialog = useCallback(() => {
    setIsSaveDialogOpen(false);
  }, []);

  const handleSaveNewView = useCallback(
    async (name: string) => {
      const success = await savedViewsHook.saveNewView(name);
      if (success) {
        setIsSaveDialogOpen(false);
      }
    },
    [savedViewsHook]
  );

  return (
    <PageLayout maxWidth="xl">
      <div className={styles["page-content"]}>
        <div className={styles["header-section"]}>
          <ResponsiveFlex direction="column" gapSize="xl">
            <ResponsiveFlex direction="column" gapSize="l">
              <div>
                <h1 className="fontSize--xxl fontWeight--bold margin--bottom--s margin--top--none">
                  {l10n.getString("transaction-export-advanced-title")}
                </h1>
                <p className="fontColor--secondary margin--none">
                  {l10n.getString("transaction-export-advanced-description")}
                </p>
              </div>
              <hr className="border--top margin--all--none" />
            </ResponsiveFlex>

            <FilterBar
              accounts={allAccounts}
              selectedAccountIds={selectedAccountIds}
              onAccountSelectionChange={setSelectedAccountIds}
              startDate={startDate}
              endDate={endDate}
              selectedPreset={selectedPreset}
              onDateRangeChange={handleDateRangeChange}
              selectedTypeCodes={selectedTypeCodes}
              onTypeCodesChange={setSelectedTypeCodes}
              transactionTypes={transactionTypes}
              transactionTypesLoading={isTypesLoading}
              transactionTypesError={typesError}
              hasActiveFilters={hasActiveFilters}
              onClearFilters={handleClearFilters}
              savedViews={savedViewsHook.views}
              savedViewsLoading={savedViewsHook.isLoading}
              selectedView={savedViewsHook.selectedView}
              onSelectView={handleSelectView}
              isModified={savedViewsHook.isModified}
              isPresetView={savedViewsHook.isPresetView}
              onSaveNewView={handleOpenSaveDialog}
              onUpdateView={savedViewsHook.updateView}
            />
          </ResponsiveFlex>
        </div>

        <div className={styles["result-summary-wrapper"]}>
          <ResultSummary
            stats={stats}
            isLoading={isStatsLoading}
            hasFiltersApplied={hasFiltersApplied}
            onExport={() => setIsExportDialogOpen(true)}
          />
        </div>
      </div>

      <SaveViewDialog
        isOpen={isSaveDialogOpen}
        onClose={handleCloseSaveDialog}
        onSave={handleSaveNewView}
        validateName={savedViewsHook.validateName}
      />

      <ExportDialog
        isOpen={isExportDialogOpen}
        onClose={() => setIsExportDialogOpen(false)}
        selectedFormat={selectedFormat}
        onFormatChange={setSelectedFormat}
        onDownload={handleDialogDownload}
        isExporting={isExporting}
      />
    </PageLayout>
  );
};

export default TransactionExportPage;
